"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { APISearchSelect, APISearchSelectContent, APISearchSelectTrigger } from "@/components/ui/select/api-search-select";
import { formatAddress, formatPhoneNumber } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { type PharmacySearchCandidate, pharmacySearchPlan } from "@/lib/pharmacy-search";
import type { DoseSpotPharmacySearchParams, DoseSpotPharmacySearchResponse, DoseSpotPharmacySnapshot } from "@/types/dosespot";
import { searchDoseSpotPharmacies } from "@/api/dosespot/pharmacies";

/** The two-line address block used inside the search results and the read-back card. */
function pharmacyAddressNode(pharmacy: DoseSpotPharmacySnapshot) {
  return formatAddress(pharmacy.address1, pharmacy.city, pharmacy.state, pharmacy.zipCode, pharmacy.address2);
}

/** The same address on one line, for callers that need a plain string. */
export function pharmacyAddressText(pharmacy: DoseSpotPharmacySnapshot) {
  return [pharmacy.address1, pharmacy.address2, pharmacy.city, pharmacy.state, pharmacy.zipCode].filter(Boolean).join(", ");
}
function pharmacyDisplayName(pharmacy: DoseSpotPharmacySnapshot) {
  const location = [pharmacy.city, pharmacy.state, pharmacy.zipCode].filter(Boolean).join(", ");
  return location ? `${pharmacy.storeName} — ${location}` : pharmacy.storeName;
}
type SearchFn = (params: DoseSpotPharmacySearchParams) => Promise<DoseSpotPharmacySearchResponse | null>;

/**
 * Runs the query plan for one term and merges the hits, keeping the first
 * candidate's ordering. Only surfaces an error when every candidate failed.
 */
async function runSearchPlan(term: string, search: SearchFn) {
  const candidates = pharmacySearchPlan(term);
  const merged: DoseSpotPharmacySnapshot[] = [];
  const seen = new Set<number>();
  let attempts = 0;
  let firstError: unknown;
  const runGroup = async (group: PharmacySearchCandidate[]) => {
    const settled = await Promise.allSettled(group.map(async candidate => (await search(candidate.params))?.items ?? []));
    for (const result of settled) {
      attempts += 1;
      if (result.status === "rejected") {
        firstError = firstError ?? result.reason;
        continue;
      }
      for (const pharmacy of result.value) {
        if (seen.has(pharmacy.pharmacyId)) continue;
        seen.add(pharmacy.pharmacyId);
        merged.push(pharmacy);
      }
    }
  };
  await runGroup(candidates.filter(candidate => !candidate.deferred));
  if (merged.length === 0) {
    await runGroup(candidates.filter(candidate => candidate.deferred));
  }
  if (merged.length === 0 && firstError && attempts > 0) throw firstError;
  return merged;
}
interface PharmacySearchSelectProps {
  value?: DoseSpotPharmacySnapshot;
  onValueChange: (pharmacy?: DoseSpotPharmacySnapshot) => void;
  /**
   * Overrides the search call. Patient-facing form fill goes through the patient
   * endpoint instead of the provider one; everything else uses the default.
   */
  search?: (params: DoseSpotPharmacySearchParams) => Promise<DoseSpotPharmacySearchResponse | null>;
  hasError?: boolean;
  errorMessage?: string;
  dataTestId?: string;
}

/**
 * Type-ahead over the DoseSpot pharmacy directory, shared by the pharmacy form question
 * and the patient chart. Owns the search itself (the query plan for a term and
 * surfacing search failures) so callers only handle the selected pharmacy.
 */
export default function PharmacySearchSelect(t0) {
  const $ = _c(16);
  const {
    value,
    onValueChange,
    search: t1,
    hasError,
    errorMessage,
    dataTestId
  } = t0;
  const search = t1 === undefined ? searchDoseSpotPharmacies : t1;
  const [searchError, setSearchError] = useState(null);
  let t2;
  if ($[0] !== search) {
    t2 = {
      mutationFn: term => runSearchPlan(term, search)
    };
    $[0] = search;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    mutateAsync: searchPharmaciesMutation
  } = useMutation(t2);
  let t3;
  if ($[2] !== searchPharmaciesMutation) {
    t3 = async rawValue => {
      const term_0 = rawValue.trim();
      setSearchError(null);
      if (term_0.length < 2) {
        return [];
      }
      ;
      try {
        return await searchPharmaciesMutation(term_0);
      } catch (t4) {
        const error = t4;
        setSearchError(await parseError(error, error instanceof Error ? error.message : "Unable to search pharmacies. Please try again."));
        return [];
      }
    };
    $[2] = searchPharmaciesMutation;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const searchPharmacies = t3;
  const formatPharmacyOption = _temp;
  const t4 = hasError || !!searchError;
  const t5 = errorMessage || searchError || undefined;
  let t6;
  if ($[4] !== dataTestId || $[5] !== t4 || $[6] !== t5) {
    t6 = <APISearchSelectTrigger placeholder="Search by pharmacy name, address, city, or ZIP code" hasError={t4} errorMessage={t5} dataTestId={dataTestId} />;
    $[4] = dataTestId;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = searchError ?? "No pharmacies found. Try another name, city, or ZIP code.";
  let t8;
  if ($[8] !== t7) {
    t8 = <APISearchSelectContent emptyMessage={t7} />;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== onValueChange || $[11] !== searchPharmacies || $[12] !== t6 || $[13] !== t8 || $[14] !== value) {
    t9 = <APISearchSelect value={value} onValueChange={onValueChange} onSearch={searchPharmacies} formatOption={formatPharmacyOption}>{t6}{t8}</APISearchSelect>;
    $[10] = onValueChange;
    $[11] = searchPharmacies;
    $[12] = t6;
    $[13] = t8;
    $[14] = value;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp(pharmacy) {
  return {
    id: String(pharmacy.pharmacyId),
    name: pharmacyDisplayName(pharmacy),
    optionNode: <div className="min-w-0"><div className="truncate font-medium text-foreground">{pharmacy.storeName}</div><div className="mt-0.5 truncate text-sm text-muted-foreground">{pharmacyAddressNode(pharmacy)}</div>{pharmacy.primaryPhone && <div className="mt-0.5 text-sm text-muted-foreground">{formatPhoneNumber(pharmacy.primaryPhone)}</div>}</div>
  };
}
interface PharmacyDetailsProps {
  pharmacy: DoseSpotPharmacySnapshot;
  label: string;
}

/** The read-back card shown under the search once a pharmacy is picked. */
export function PharmacyDetails(t0) {
  const $ = _c(15);
  const {
    pharmacy,
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pharmacy.storeName) {
    t2 = <div className="mt-1 font-medium text-foreground">{pharmacy.storeName}</div>;
    $[2] = pharmacy.storeName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== pharmacy) {
    t3 = pharmacyAddressNode(pharmacy);
    $[4] = pharmacy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="mt-1 text-muted-foreground">{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== pharmacy.primaryPhone) {
    t5 = pharmacy.primaryPhone && <div className="mt-1 text-muted-foreground">{formatPhoneNumber(pharmacy.primaryPhone)}</div>;
    $[8] = pharmacy.primaryPhone;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="rounded-lg border border-border bg-background p-3 text-sm">{t1}{t2}{t4}{t5}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}